import React, { useState, useEffect, useCallback } from 'react';
import api from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { CheckCircle2, Circle, MapPin, Phone, Package, RefreshCw, Route, List, ChevronDown, Navigation, Building2, Clock, GripVertical, Save } from 'lucide-react';

export default function DeliveriesPage() {
  const { user } = useAuth();
  const [data, setData] = useState({ deliveries: [], date: '' });
  const [routeData, setRouteData] = useState(null);
  const [deliveryBoys, setDeliveryBoys] = useState([]);
  const [selectedBoy, setSelectedBoy] = useState('');
  const [viewMode, setViewMode] = useState('route'); // 'route' or 'list'
  const [loading, setLoading] = useState(true);
  const [aptOrder, setAptOrder] = useState([]);
  const [showRouteConfig, setShowRouteConfig] = useState(false);
  const [dragIdx, setDragIdx] = useState(null);

  const isDeliveryBoy = user?.role === 'delivery_boy';

  const fetchData = useCallback(async () => {
    try {
      const [delivRes, routeRes] = await Promise.all([
        api.getTodayDeliveries(),
        api.getOptimizedRoute(isDeliveryBoy ? undefined : selectedBoy || undefined)
      ]);
      setData(delivRes.data);
      setRouteData(routeRes.data);
      if (!isDeliveryBoy) {
        const boysRes = await api.getDeliveryBoys();
        setDeliveryBoys(boysRes.data);
      }
    } catch (err) { console.error(err); }
    setLoading(false);
  }, [selectedBoy, isDeliveryBoy]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Load route config when boy selected
  useEffect(() => {
    if (selectedBoy && showRouteConfig) {
      api.getRouteConfig(selectedBoy).then(res => {
        setAptOrder(res.data.apartmentOrder || []);
      }).catch(() => setAptOrder([]));
    }
  }, [selectedBoy, showRouteConfig]);

  const handleMark = async (subId) => {
    try {
      await api.markDelivered({ subscriberId: subId, date: data.date || routeData?.date });
      fetchData();
    } catch (err) { console.error(err); }
  };

  const handleSaveRoute = async () => {
    if (!selectedBoy) return;
    try {
      await api.saveRouteConfig({ deliveryBoyId: selectedBoy, apartmentOrder: aptOrder });
      setShowRouteConfig(false);
      setLoading(true);
      fetchData();
    } catch (err) { console.error(err); }
  };

  const handleDragStart = (idx) => setDragIdx(idx);
  const handleDragOver = (e, idx) => {
    e.preventDefault();
    if (dragIdx === null || dragIdx === idx) return;
    const newOrder = [...aptOrder];
    const item = newOrder.splice(dragIdx, 1)[0];
    newOrder.splice(idx, 0, item);
    setAptOrder(newOrder);
    setDragIdx(idx);
  };
  const handleDragEnd = () => setDragIdx(null);

  const pendingCount = data.deliveries.filter(d => d.deliveryStatus === 'pending').length;
  const deliveredCount = data.deliveries.filter(d => d.deliveryStatus === 'delivered').length;

  const allApartments = [...new Set((routeData?.route || []).map(s => s.apartment))];
  const missingApts = allApartments.filter(a => !aptOrder.includes(a));

  return (
    <div data-testid="deliveries-panel" className="space-y-5">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold tracking-tight" style={{ fontFamily: 'Outfit', color: '#F5F5F4' }}>
            {isDeliveryBoy ? 'My Route' : "Today's Deliveries"}
          </h2>
          <p className="text-sm mt-1" style={{ color: '#A8A29E' }}>
            {routeData?.date || data.date} &middot; {routeData?.stops || 0} stops &middot; {routeData?.totalDeliveries || 0} deliveries
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          {/* View mode toggle */}
          <div className="flex rounded-lg overflow-hidden" style={{ border: '1px solid #2A241F' }}>
            <button data-testid="view-route" onClick={() => setViewMode('route')}
              className="px-3 py-2 text-sm font-medium flex items-center gap-1.5 transition-all"
              style={{ background: viewMode === 'route' ? 'rgba(200,169,110,0.15)' : '#1E1A17', color: viewMode === 'route' ? '#C8A96E' : '#78716C' }}>
              <Route className="w-3.5 h-3.5" /> Route
            </button>
            <button data-testid="view-list" onClick={() => setViewMode('list')}
              className="px-3 py-2 text-sm font-medium flex items-center gap-1.5 transition-all"
              style={{ background: viewMode === 'list' ? 'rgba(200,169,110,0.15)' : '#1E1A17', color: viewMode === 'list' ? '#C8A96E' : '#78716C' }}>
              <List className="w-3.5 h-3.5" /> List
            </button>
          </div>

          {!isDeliveryBoy && (
            <div className="relative">
              <select data-testid="route-boy-filter" value={selectedBoy} onChange={e => { setSelectedBoy(e.target.value); setLoading(true); }}
                className="px-3 py-2 rounded-lg text-sm appearance-none pr-8" style={{ background: '#1E1A17', border: '1px solid #2A241F', color: '#F5F5F4' }}>
                <option value="">All Delivery Boys</option>
                {deliveryBoys.map(b => <option key={b._id} value={b._id}>{b.name}</option>)}
              </select>
              <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 pointer-events-none" style={{ color: '#78716C' }} />
            </div>
          )}

          <button data-testid="refresh-deliveries" onClick={() => { setLoading(true); fetchData(); }}
            className="px-3 py-2 rounded-lg text-sm font-medium transition-all flex items-center gap-1.5"
            style={{ background: '#2A241F', color: '#C8A96E', border: '1px solid rgba(200,169,110,0.2)' }}>
            <RefreshCw className="w-3.5 h-3.5" /> Refresh
          </button>
        </div>
      </div>

      {/* Progress bar */}
      {routeData && routeData.totalDeliveries > 0 && (
        <div className="rounded-xl p-4" style={{ background: '#1E1A17', border: '1px solid rgba(200,169,110,0.2)' }}>
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium" style={{ color: '#F5F5F4' }}>Route Progress</span>
            <span className="text-sm font-bold" style={{ color: '#C8A96E' }}>{routeData.progress}%</span>
          </div>
          <div className="h-3 rounded-full overflow-hidden" style={{ background: '#2A241F' }}>
            <div className="h-full rounded-full transition-all duration-500" style={{ width: `${routeData.progress}%`, background: routeData.progress === 100 ? '#4ADE80' : '#C8A96E' }} />
          </div>
          <div className="flex justify-between mt-2 text-xs" style={{ color: '#78716C' }}>
            <span>{routeData.totalDelivered} delivered</span>
            <span>{routeData.totalDeliveries - routeData.totalDelivered} remaining</span>
          </div>
        </div>
      )}

      {/* Summary cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className="rounded-xl p-4" style={{ background: '#1E1A17', border: '1px solid rgba(200,169,110,0.2)' }}>
          <div className="text-2xl font-bold" style={{ fontFamily: 'Outfit', color: '#F5F5F4' }}>{routeData?.stops || 0}</div>
          <div className="text-xs mt-1" style={{ color: '#A8A29E' }}>Apartment Stops</div>
        </div>
        <div className="rounded-xl p-4" style={{ background: '#1E1A17', border: '1px solid rgba(200,169,110,0.2)' }}>
          <div className="text-2xl font-bold" style={{ fontFamily: 'Outfit', color: '#C8A96E' }}>{pendingCount}</div>
          <div className="text-xs mt-1" style={{ color: '#A8A29E' }}>Pending</div>
        </div>
        <div className="rounded-xl p-4" style={{ background: '#1E1A17', border: '1px solid rgba(200,169,110,0.2)' }}>
          <div className="text-2xl font-bold" style={{ fontFamily: 'Outfit', color: '#4ADE80' }}>{deliveredCount}</div>
          <div className="text-xs mt-1" style={{ color: '#A8A29E' }}>Delivered</div>
        </div>
        <div className="rounded-xl p-4" style={{ background: '#1E1A17', border: '1px solid rgba(200,169,110,0.2)' }}>
          <div className="text-2xl font-bold" style={{ fontFamily: 'Outfit', color: '#FBBF24' }}>{data.deliveries.filter(d => d.isSkipped || d.isOOT).length}</div>
          <div className="text-xs mt-1" style={{ color: '#A8A29E' }}>Skipped / OOT</div>
        </div>
      </div>

      {/* Route Config Button */}
      {!isDeliveryBoy && selectedBoy && (
        <button data-testid="configure-route-button" onClick={() => {
          setShowRouteConfig(!showRouteConfig);
          if (!showRouteConfig) {
            const currentApts = (routeData?.route || []).map(s => s.apartment);
            if (aptOrder.length === 0) setAptOrder(currentApts);
          }
        }}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all"
          style={{ background: '#2A241F', color: '#C8A96E', border: '1px solid rgba(200,169,110,0.2)' }}>
          <Navigation className="w-4 h-4" /> {showRouteConfig ? 'Hide Route Config' : 'Configure Apartment Order'}
        </button>
      )}

      {/* Route Config - Drag to reorder */}
      {showRouteConfig && (
        <div className="rounded-xl p-5" style={{ background: '#1E1A17', border: '1px solid rgba(200,169,110,0.2)' }}>
          <h4 className="text-sm font-semibold mb-3" style={{ fontFamily: 'Outfit', color: '#C8A96E' }}>Drag to set apartment delivery order</h4>
          <div className="space-y-2">
            {[...aptOrder, ...missingApts].map((apt, idx) => (
              <div key={apt} draggable onDragStart={() => handleDragStart(idx)} onDragOver={(e) => handleDragOver(e, idx)} onDragEnd={handleDragEnd}
                className="flex items-center gap-3 p-3 rounded-lg cursor-grab active:cursor-grabbing transition-all"
                style={{ background: dragIdx === idx ? 'rgba(200,169,110,0.1)' : '#100E0C', border: '1px solid #2A241F' }}>
                <GripVertical className="w-4 h-4 flex-shrink-0" style={{ color: '#78716C' }} />
                <span className="w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0" style={{ background: 'rgba(200,169,110,0.15)', color: '#C8A96E' }}>{idx + 1}</span>
                <Building2 className="w-4 h-4 flex-shrink-0" style={{ color: '#A8A29E' }} />
                <span className="text-sm" style={{ color: '#F5F5F4' }}>{apt}</span>
              </div>
            ))}
          </div>
          <button data-testid="save-route-config" onClick={handleSaveRoute} className="mt-4 inline-flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-semibold transition-all" style={{ background: '#C8A96E', color: '#000' }}>
            <Save className="w-4 h-4" /> Save Route Order
          </button>
        </div>
      )}

      {loading ? (
        <div className="text-center py-12" style={{ color: '#78716C' }}>Loading deliveries...</div>
      ) : viewMode === 'route' && routeData ? (
        /* Route View - Grouped by apartment */
        <div className="space-y-4">
          {routeData.route.length === 0 ? (
            <div className="text-center py-12 rounded-xl" style={{ background: '#1E1A17', border: '1px solid rgba(200,169,110,0.2)', color: '#78716C' }}>
              <Package className="w-12 h-12 mx-auto mb-3" style={{ color: '#2A241F' }} />
              <p>{routeData.message || 'No deliveries for today'}</p>
            </div>
          ) : routeData.route.map((stop, stopIdx) => {
            const allDone = stop.delivered === stop.total;
            return (
              <div key={stop.apartment} data-testid={`route-stop-${stopIdx}`} className="rounded-xl overflow-hidden fade-in" style={{
                background: '#1E1A17',
                border: `1px solid ${allDone ? 'rgba(74,222,128,0.3)' : 'rgba(200,169,110,0.2)'}`,
                animationDelay: `${stopIdx * 80}ms`
              }}>
                {/* Stop header */}
                <div className="px-5 py-3 flex items-center gap-3" style={{ borderBottom: '1px solid #2A241F', background: allDone ? 'rgba(74,222,128,0.05)' : 'rgba(200,169,110,0.03)' }}>
                  <div className="w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0" style={{
                    background: allDone ? 'rgba(74,222,128,0.15)' : 'rgba(200,169,110,0.15)',
                    color: allDone ? '#4ADE80' : '#C8A96E'
                  }}>
                    {stop.stopNumber}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <Building2 className="w-4 h-4" style={{ color: allDone ? '#4ADE80' : '#C8A96E', strokeWidth: 1.5 }} />
                      <span className="font-semibold text-sm" style={{ color: '#F5F5F4' }}>{stop.apartment}</span>
                    </div>
                  </div>
                  <div className="text-right">
                    <span className="text-sm font-bold" style={{ color: allDone ? '#4ADE80' : '#F5F5F4' }}>{stop.delivered}/{stop.total}</span>
                    <div className="text-xs" style={{ color: '#78716C' }}>deliveries</div>
                  </div>
                </div>

                {/* Deliveries in this stop */}
                <div className="divide-y" style={{ borderColor: '#2A241F' }}>
                  {stop.deliveries.map((d) => {
                    const isDelivered = d.deliveryStatus === 'delivered';
                    return (
                      <div key={d.id} className="px-5 py-3 flex items-center gap-3 transition-colors" style={{ background: isDelivered ? 'rgba(74,222,128,0.02)' : 'transparent' }}>
                        <span className="w-6 h-6 rounded-full flex items-center justify-center text-xs font-medium flex-shrink-0" style={{ background: '#100E0C', color: '#78716C', border: '1px solid #2A241F' }}>
                          {d.sequence}
                        </span>
                        <button data-testid={`mark-delivered-${d.id}`} onClick={() => handleMark(d.id)}
                          className="flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center transition-all"
                          style={{ background: isDelivered ? 'rgba(74,222,128,0.15)' : 'rgba(200,169,110,0.1)', border: `1px solid ${isDelivered ? 'rgba(74,222,128,0.3)' : 'rgba(200,169,110,0.2)'}` }}>
                          {isDelivered ? <CheckCircle2 className="w-4 h-4" style={{ color: '#4ADE80' }} /> : <Circle className="w-4 h-4" style={{ color: '#C8A96E' }} />}
                        </button>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="font-medium text-sm" style={{ color: isDelivered ? '#A8A29E' : '#F5F5F4', textDecoration: isDelivered ? 'line-through' : 'none' }}>{d.name}</span>
                            <span className="text-xs px-1.5 py-0.5 rounded" style={{ background: '#100E0C', color: '#78716C', border: '1px solid #2A241F' }}>Flat {d.flat}</span>
                            {d.quantity > 1 && <span className="px-1.5 py-0.5 rounded-full text-xs font-semibold" style={{ background: 'rgba(200,169,110,0.15)', color: '#C8A96E' }}>x{d.quantity}</span>}
                          </div>
                          <div className="flex items-center gap-3 mt-1 text-xs" style={{ color: '#78716C' }}>
                            <span className="inline-flex items-center gap-1"><Clock className="w-3 h-3" />{d.slot}</span>
                            <a href={`tel:${d.mobile}`} className="inline-flex items-center gap-1" style={{ color: '#C8A96E' }}><Phone className="w-3 h-3" />{d.mobile}</a>
                          </div>
                        </div>
                        {isDelivered && <span className="badge-delivered px-2 py-0.5 rounded-full text-xs font-semibold flex-shrink-0">Done</span>}
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        /* List View - flat list */
        <div className="space-y-3">
          {data.deliveries.length === 0 ? (
            <div className="text-center py-12 rounded-xl" style={{ background: '#1E1A17', border: '1px solid rgba(200,169,110,0.2)', color: '#78716C' }}>
              <Package className="w-12 h-12 mx-auto mb-3" style={{ color: '#2A241F' }} />
              <p>{data.message || 'No deliveries for today'}</p>
            </div>
          ) : data.deliveries.map((d, i) => {
            const isDelivered = d.deliveryStatus === 'delivered';
            const isSkipped = d.isSkipped;
            const isOOT = d.isOOT;
            return (
              <div key={d.id} data-testid={`delivery-card-${d.id}`} className="rounded-xl p-4 transition-all duration-200 fade-in"
                style={{ background: '#1E1A17', border: `1px solid ${isDelivered ? 'rgba(74,222,128,0.3)' : 'rgba(200,169,110,0.2)'}`, animationDelay: `${i * 50}ms`, opacity: isSkipped || isOOT ? 0.6 : 1 }}>
                <div className="flex items-center gap-4">
                  <button data-testid={`mark-delivered-${d.id}`} onClick={() => handleMark(d.id)} disabled={isSkipped || isOOT}
                    className="flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center transition-all"
                    style={{ background: isDelivered ? 'rgba(74,222,128,0.15)' : 'rgba(200,169,110,0.1)', border: `1px solid ${isDelivered ? 'rgba(74,222,128,0.3)' : 'rgba(200,169,110,0.2)'}`, cursor: isSkipped || isOOT ? 'not-allowed' : 'pointer' }}>
                    {isDelivered ? <CheckCircle2 className="w-5 h-5" style={{ color: '#4ADE80' }} /> : <Circle className="w-5 h-5" style={{ color: '#C8A96E' }} />}
                  </button>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-medium text-sm" style={{ color: '#F5F5F4' }}>{d.name}</span>
                      {d.quantity > 1 && <span className="px-2 py-0.5 rounded-full text-xs font-semibold" style={{ background: 'rgba(200,169,110,0.15)', color: '#C8A96E' }}>x{d.quantity}</span>}
                      {isSkipped && <span className="badge-skipped px-2 py-0.5 rounded-full text-xs font-semibold">Skipped</span>}
                      {isOOT && <span className="badge-oot px-2 py-0.5 rounded-full text-xs font-semibold">Out of Town</span>}
                      {isDelivered && <span className="badge-delivered px-2 py-0.5 rounded-full text-xs font-semibold">Delivered</span>}
                    </div>
                    <div className="flex items-center gap-4 mt-1.5 text-xs" style={{ color: '#78716C' }}>
                      <span className="inline-flex items-center gap-1"><MapPin className="w-3 h-3" />{d.apt}{d.flat ? ` / ${d.flat}` : ''}</span>
                      <span className="inline-flex items-center gap-1"><Phone className="w-3 h-3" />{d.mobile}</span>
                      <span>{d.slot}</span>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
